/**
 * 基层中医决策支持系统 - 前端安全工具
 * 提供输入验证、XSS防护、安全存储等功能
 */

import httpClient from "@shared/http/createHttpClient";

// 验证结果接口
interface ValidationResult {
  isValid: boolean;
  message?: string;
  cleanValue?: string;
}

// 安全工具类
class SecurityUtils {
  /**
   * HTML转义，防止XSS攻击
   */
  static escapeHtml(text: string | null | undefined): string {
    if (!text) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * 清理HTML标签
   */
  static stripHtml(html: string | null | undefined): string {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/<style[\s\S]*?<\/style>/gi, "");
    return div.textContent || div.innerText || "";
  }

  /**
   * 等保三级：净化 HTML 内容，用于 v-html 渲染 AI 输出。
   * 保留安全的格式标签（p/strong/em/ul/ol/li/table 等），
   * 移除 script/iframe/event-handler 等危险内容。
   */
  private static readonly _SAFE_TAGS = new Set([
    "p",
    "br",
    "hr",
    "div",
    "span",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "strong",
    "em",
    "b",
    "i",
    "u",
    "s",
    "del",
    "ins",
    "mark",
    "sub",
    "sup",
    "ul",
    "ol",
    "li",
    "dl",
    "dt",
    "dd",
    "table",
    "thead",
    "tbody",
    "tfoot",
    "tr",
    "th",
    "td",
    "caption",
    "colgroup",
    "col",
    "blockquote",
    "pre",
    "code",
    "a",
    "img",
    "details",
    "summary",
  ]);

  private static readonly _SAFE_ATTRS = new Set([
    "class",
    "id",
    "style",
    "title",
    "alt",
    "href",
    "src",
    "target",
    "rel",
    "colspan",
    "rowspan",
    "width",
    "height",
    "align",
    "valign",
  ]);

  static sanitizeHtml(html: string | null | undefined): string {
    if (!html) return "";
    // 用 DOMParser 解析，遍历节点树做白名单过滤
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const frag = document.createDocumentFragment();

    const cleanNode = (node: Node): Node | null => {
      if (node.nodeType === Node.TEXT_NODE) {
        return document.createTextNode(node.textContent || "");
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const tag = el.tagName.toLowerCase();
        if (!this._SAFE_TAGS.has(tag)) {
          // 保留子节点内容（递归），但丢弃不安全标签本身
          const wrapper = document.createDocumentFragment();
          for (const child of Array.from(el.childNodes)) {
            const cleaned = cleanNode(child);
            if (cleaned) wrapper.appendChild(cleaned);
          }
          return wrapper;
        }
        // 创建安全标签副本
        const safe = document.createElement(tag);
        // 过滤属性：只保留白名单 + 移除 on* 事件
        for (const attr of Array.from(el.attributes)) {
          const name = attr.name.toLowerCase();
          if (name.startsWith("on")) continue; // 事件处理器
          if (attr.value.toLowerCase().includes("javascript:")) continue; // javascript: 协议
          if (attr.value.toLowerCase().includes("data:")) continue; // data: 协议（防 data: URL XSS）
          if (!this._SAFE_ATTRS.has(name)) continue; // 非白名单属性
          safe.setAttribute(name, attr.value);
        }
        // a 标签强制 target=_blank + noopener
        if (tag === "a") {
          safe.setAttribute("target", "_blank");
          safe.setAttribute("rel", "noopener noreferrer");
        }
        for (const child of Array.from(el.childNodes)) {
          const cleaned = cleanNode(child);
          if (cleaned) safe.appendChild(cleaned);
        }
        return safe;
      }
      return null;
    };

    for (const child of Array.from(doc.body.childNodes)) {
      const cleaned = cleanNode(child);
      if (cleaned) frag.appendChild(cleaned);
    }

    const out = document.createElement("div");
    out.appendChild(frag);
    return out.innerHTML;
  }

  /**
   * 验证手机号格式
   */
  static validatePhone(phone: string | null | undefined): ValidationResult {
    if (!phone) return { isValid: false, message: "手机号不能为空" };

    // 清理非数字字符
    const cleanPhone = phone.replace(/\D/g, "");

    if (cleanPhone.length !== 11) {
      return { isValid: false, message: "手机号必须为11位数字" };
    }

    if (!cleanPhone.startsWith("1")) {
      return { isValid: false, message: "手机号必须以1开头" };
    }

    const phoneRegex = /^1[3-9]\d{9}$/;
    if (!phoneRegex.test(cleanPhone)) {
      return { isValid: false, message: "手机号格式不正确" };
    }

    return { isValid: true, cleanValue: cleanPhone };
  }

  /**
   * 验证验证码格式
   */
  static validateCode(code: string | null | undefined): ValidationResult {
    if (!code) return { isValid: false, message: "验证码不能为空" };

    // 清理非数字字符
    const cleanCode = code.replace(/\D/g, "");

    if (cleanCode.length !== 6) {
      return { isValid: false, message: "验证码必须为6位数字" };
    }

    return { isValid: true, cleanValue: cleanCode };
  }

  /**
   * 验证用户名
   */
  static validateUsername(
    username: string | null | undefined,
  ): ValidationResult {
    if (!username) return { isValid: false, message: "用户名不能为空" };

    // 长度检查
    if (username.length < 2 || username.length > 20) {
      return { isValid: false, message: "用户名长度应在2-20个字符之间" };
    }

    // 只允许中文、英文、数字和下划线
    const usernameRegex = /^[a-zA-Z0-9\u4e00-\u9fa5_]+$/;
    if (!usernameRegex.test(username)) {
      return {
        isValid: false,
        message: "用户名只能包含中文、英文、数字和下划线",
      };
    }

    return { isValid: true, cleanValue: this.escapeHtml(username.trim()) };
  }

  // ==================== 密码策略（镜像后端唯一权威源） ====================
  // 来源：backend/shared/utils/security.py :: SecurityValidator（与 pgyAdmin
  // password_policy 对齐）。客户端校验仅用于提交前提示，避免用户按页面提示
  // 输入后反被后端 400 拒绝；策略变更时必须两处同步。

  private static readonly _PASSWORD_MIN_LENGTH = 12;
  private static readonly _SEQUENTIAL_LENGTH = 4;
  private static readonly _REPEATED_LENGTH = 4;
  private static readonly _PASSWORD_UPPER = /[A-Z]/;
  private static readonly _PASSWORD_LOWER = /[a-z]/;
  private static readonly _PASSWORD_DIGIT = /\d/;
  private static readonly _PASSWORD_SPECIAL =
    /[!@#$%^&*()_+\-=[\]{};:'",.<>?/\\|`~]/;
  private static readonly _PASSWORD_SEQUENCES = [
    "abcdefghijklmnopqrstuvwxyz",
    "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
    "0123456789",
    "qwertyuiop",
    "asdfghjkl",
    "zxcvbnm",
  ];

  /**
   * 检测 4 位及以上连续字符（含逆序，如 abcd / dcba / 1234 / qwer）
   */
  private static hasSequentialChars(password: string): boolean {
    const n = SecurityUtils._SEQUENTIAL_LENGTH;
    return SecurityUtils._PASSWORD_SEQUENCES.some((seq) => {
      for (let i = 0; i <= seq.length - n; i++) {
        const chunk = seq.slice(i, i + n);
        const reversed = chunk.split("").reverse().join("");
        if (password.includes(chunk) || password.includes(reversed)) {
          return true;
        }
      }
      return false;
    });
  }

  /**
   * 检测 4 位及以上重复字符（如 aaaa）
   */
  private static hasRepeatedChars(password: string): boolean {
    const n = SecurityUtils._REPEATED_LENGTH;
    for (let i = 0; i <= password.length - n; i++) {
      const chunk = password.slice(i, i + n);
      if (chunk === chunk[0].repeat(n)) {
        return true;
      }
    }
    return false;
  }

  /**
   * 验证密码复杂度（等保三级：≥12 位；大写、小写、数字、特殊字符四类全含；
   * 无 4 位及以上连续/重复字符），规则与后端
   * SecurityValidator.validate_password_complexity 一致。
   *
   * 说明：后端另有弱密码表与「不含用户名」检查——弱密码表全部条目均不足
   * 12 位（长度检查已覆盖）；两个改密端点（set-password /
   * first-password-change）调用复杂度校验时未传入用户名，故客户端不重复。
   */
  static validatePassword(
    password: string | null | undefined,
  ): ValidationResult {
    if (!password) return { isValid: false, message: "密码不能为空" };

    if (password.length < SecurityUtils._PASSWORD_MIN_LENGTH) {
      return {
        isValid: false,
        message: `密码长度至少为 ${SecurityUtils._PASSWORD_MIN_LENGTH} 位`,
      };
    }

    const missing: string[] = [];
    if (!SecurityUtils._PASSWORD_UPPER.test(password)) missing.push("大写字母");
    if (!SecurityUtils._PASSWORD_LOWER.test(password)) missing.push("小写字母");
    if (!SecurityUtils._PASSWORD_DIGIT.test(password)) missing.push("数字");
    if (!SecurityUtils._PASSWORD_SPECIAL.test(password))
      missing.push("特殊字符");
    if (missing.length > 0) {
      return {
        isValid: false,
        message: `密码需同时包含大写字母、小写字母、数字、特殊字符，当前缺少：${missing.join("、")}`,
      };
    }

    if (SecurityUtils.hasSequentialChars(password)) {
      return {
        isValid: false,
        message: "密码不能包含 4 个或更多连续字符（如 abcd、1234）",
      };
    }

    if (SecurityUtils.hasRepeatedChars(password)) {
      return {
        isValid: false,
        message: "密码不能包含 4 个或更多重复字符（如 aaaa）",
      };
    }

    return { isValid: true };
  }

  /**
   * 安全的字符串清理
   */
  static sanitizeString(
    text: string | null | undefined,
    maxLength: number = 1000,
  ): string {
    if (!text) return "";

    // 限制长度
    let cleanText = text.slice(0, maxLength);

    // HTML转义
    cleanText = this.escapeHtml(cleanText);

    // 移除潜在危险的模式
    const dangerousPatterns = [
      /<script[^>]*>.*?<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /<iframe[^>]*>.*?<\/iframe>/gi,
      /data:/gi,
    ];

    dangerousPatterns.forEach((pattern) => {
      cleanText = cleanText.replace(pattern, "");
    });

    return cleanText.trim();
  }

  /**
   * 验证URL是否安全
   */
  static isSecureUrl(url: string | null | undefined): boolean {
    if (!url) return false;

    try {
      const urlObj = new URL(url);
      // 只允许http和https协议
      return ["http:", "https:"].includes(urlObj.protocol);
    } catch {
      return false;
    }
  }

  /**
   * 生成随机字符串（用于CSRF token等）
   */
  static generateRandomString(length: number = 32): string {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * 脱敏显示手机号
   */
  static maskPhone(phone: string | null | undefined): string {
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) return "";
    return phone.substring(0, 3) + "****" + phone.substring(7);
  }

  /**
   * 脱敏显示姓名
   */
  static maskName(name: string | null | undefined): string {
    if (!name) return "";
    if (name.length <= 1) return name;
    if (name.length === 2) return name[0] + "*";
    return name[0] + "*".repeat(name.length - 2) + name[name.length - 1];
  }
}

// 安全存储类 - 防止敏感信息明文存储
class SecureStorage {
  /**
   * 简单的Base64编码（不是加密，只是编码）
   */
  static encode(data: unknown): string | null {
    try {
      return btoa(encodeURIComponent(JSON.stringify(data)));
    } catch {
      return null;
    }
  }

  /**
   * 解码Base64数据
   */
  static decode(encodedData: string): unknown | null {
    try {
      return JSON.parse(decodeURIComponent(atob(encodedData)));
    } catch {
      return null;
    }
  }

  /**
   * 安全设置存储项
   */
  static setItem(
    key: string,
    value: unknown,
    useSessionStorage: boolean = false,
  ): boolean {
    try {
      const encoded = this.encode(value);
      if (encoded) {
        const storage = useSessionStorage ? sessionStorage : localStorage;
        storage.setItem(key, encoded);
        return true;
      }
    } catch (error) {
      console.error("存储失败:", error);
    }
    return false;
  }

  /**
   * 安全获取存储项
   */
  static getItem(key: string, useSessionStorage: boolean = false) {
    try {
      const storage = useSessionStorage ? sessionStorage : localStorage;
      const encoded = storage.getItem(key);
      if (encoded) {
        return this.decode(encoded);
      }
    } catch (error) {
      console.error("读取存储失败:", error);
    }
    return null;
  }

  /**
   * 删除存储项
   */
  static removeItem(key: string, useSessionStorage: boolean = false): boolean {
    try {
      const storage = useSessionStorage ? sessionStorage : localStorage;
      storage.removeItem(key);
      return true;
    } catch (error) {
      console.error("删除存储失败:", error);
    }
    return false;
  }

  /**
   * 清除所有存储
   */
  static clear(useSessionStorage: boolean = false): boolean {
    try {
      const storage = useSessionStorage ? sessionStorage : localStorage;
      storage.clear();
      return true;
    } catch (error) {
      console.error("清除存储失败:", error);
    }
    return false;
  }
}

// 请求选项接口
interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  [key: string]: unknown;
}

// 表单数据接口
interface FormData {
  phone?: string;
  code?: string;
  username?: string;
  [key: string]: unknown;
}

// 验证错误接口
interface ValidationErrors {
  [key: string]: string;
}

// 表单验证结果接口
interface FormValidationResult {
  isValid: boolean;
  errors: ValidationErrors;
  cleanData: FormData;
}

// 请求安全类
class RequestSecurity {
  /**
   * 添加安全头部
   */
  static getSecureHeaders(): Record<string, string> {
    return {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      // 添加CSRF token（如果有的话）
      "X-CSRF-Token": this.getCSRFToken(),
    };
  }

  /**
   * 获取CSRF token
   */
  static getCSRFToken(): string {
    // 从cookie或meta标签中获取CSRF token
    const token = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");
    return token || "";
  }

  /**
   * 安全的API请求包装（使用 createHttpClient）
   */
  static async secureRequest(
    url: string,
    options: RequestOptions = {},
  ): Promise<unknown> {
    let validatedData: unknown;

    // 如果是POST请求且有数据，进行输入验证
    if (options.method === "POST" && options.body) {
      try {
        const data = JSON.parse(options.body);
        validatedData = this.validateRequestData(data);
      } catch (error) {
        console.error("请求数据验证失败:", error);
        throw new Error("请求数据格式不正确");
      }
    }

    // 使用 createHttpClient 进行安全请求，自动处理认证和错误
    return httpClient.post(url, validatedData, { silent: true });
  }

  /**
   * 验证请求数据
   */
  static validateRequestData(data: unknown): unknown {
    const validatedData: Record<string, unknown> = {};
    if (data === null || typeof data !== "object") {
      return data;
    }

    for (const [key, value] of Object.entries(data)) {
      if (typeof value === "string") {
        // 清理字符串数据
        validatedData[key] = SecurityUtils.sanitizeString(value);
      } else if (typeof value === "number") {
        // 验证数字
        validatedData[key] = isFinite(value) ? value : 0;
      } else if (Array.isArray(value)) {
        // 验证数组
        validatedData[key] = value.slice(0, 100); // 限制数组长度
      } else if (value !== null && typeof value === "object") {
        // 递归验证对象
        validatedData[key] = this.validateRequestData(value);
      } else {
        validatedData[key] = value;
      }
    }

    return validatedData;
  }
}

// 表单安全类
class FormSecurity {
  /**
   * 防止表单重复提交
   */
  static preventDoubleSubmit(
    form: HTMLFormElement,
    timeout: number = 2000,
  ): void {
    const submitButtons = form.querySelectorAll(
      'button[type="submit"], input[type="submit"]',
    ) as NodeListOf<HTMLButtonElement | HTMLInputElement>;

    const disableButtons = () => {
      submitButtons.forEach((btn) => {
        btn.disabled = true;
        btn.dataset.originalText =
          btn.textContent || (btn as HTMLInputElement).value;
        if (btn.textContent !== null) {
          btn.textContent = "提交中...";
        } else {
          (btn as HTMLInputElement).value = "提交中...";
        }
      });
    };

    const enableButtons = () => {
      submitButtons.forEach((btn) => {
        btn.disabled = false;
        if (btn.dataset.originalText) {
          if (btn.textContent !== null) {
            btn.textContent = btn.dataset.originalText;
          } else {
            (btn as HTMLInputElement).value = btn.dataset.originalText;
          }
        }
      });
    };

    disableButtons();
    setTimeout(enableButtons, timeout);
  }

  /**
   * 表单数据验证
   */
  static validateFormData(formData: FormData): FormValidationResult {
    const errors: ValidationErrors = {};

    // 手机号验证
    if (formData.phone) {
      const phoneResult = SecurityUtils.validatePhone(formData.phone);
      if (!phoneResult.isValid) {
        errors.phone = phoneResult.message!;
      } else {
        formData.phone = phoneResult.cleanValue;
      }
    }

    // 验证码验证
    if (formData.code) {
      const codeResult = SecurityUtils.validateCode(formData.code);
      if (!codeResult.isValid) {
        errors.code = codeResult.message!;
      } else {
        formData.code = codeResult.cleanValue;
      }
    }

    // 用户名验证
    if (formData.username) {
      const usernameResult = SecurityUtils.validateUsername(formData.username);
      if (!usernameResult.isValid) {
        errors.username = usernameResult.message!;
      } else {
        formData.username = usernameResult.cleanValue;
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
      cleanData: formData,
    };
  }
}

// 导出类和工具函数
export { SecurityUtils, SecureStorage, RequestSecurity, FormSecurity };

// 默认导出主要的安全工具类
export default SecurityUtils;
